<!-- Authors Header -->
<div class="flex flex-col">
  <!-- Title and Desktop Sort -->
  <div
    class="mb-2 flex flex-col lg:mb-6 lg:flex-row lg:items-center lg:justify-between"
  >
    <div class="text-2xl leading-tight font-medium text-neutral-800">
      {{ 'pages.authors.authorCount' | translate: { count: totalItems() } }}
    </div>
    <!-- Sort Button - Desktop Only -->
    <div class="relative hidden items-center justify-center gap-2 lg:flex">
      <button
        class="flex items-center gap-2 rounded-full px-3 py-2 transition-colors hover:bg-neutral-50"
        (click)="toggleSortDropdown()"
      >
        <span class="text-base leading-normal font-medium text-neutral-800">
          {{ 'pages.authors.sortBy' | translate }}:
        </span>
        <span class="text-base leading-normal font-bold text-neutral-800">
          {{
            sortOrder === 'asc'
              ? ('pages.authors.ascending' | translate)
              : ('pages.authors.descending' | translate)
          }}
        </span>
        <div class="relative h-6 w-6 overflow-hidden">
          <svg
            class="h-4 w-4 transform transition-transform"
            [class.rotate-180]="showSortDropdown()"
            [attr.aria-label]="'pages.authors.expandIcon' | translate"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      <!-- Dropdown Menu - Desktop -->
      @if (showSortDropdown()) {
        <div
          class="sort-dropdown shadow-lg absolute top-full right-0 z-10 mt-2 rounded-lg border border-neutral-200 bg-white py-2"
        >
          <button
            class="w-full px-4 py-2 text-left transition-colors hover:bg-neutral-50"
            [class.font-bold]="sortOrder === 'asc'"
            (click)="setSortOrder('asc')"
          >
            <span class="text-base leading-normal text-neutral-800">
              {{ 'pages.authors.ascending' | translate }}
            </span>
          </button>
          <button
            class="w-full px-4 py-2 text-left transition-colors hover:bg-neutral-50"
            [class.font-bold]="sortOrder === 'desc'"
            (click)="setSortOrder('desc')"
          >
            <span class="text-base leading-normal text-neutral-800">
              {{ 'pages.authors.descending' | translate }}
            </span>
          </button>
        </div>
      }
    </div>
  </div>
</div>

<!-- Sort Button - Mobile Only -->
<div class="relative mb-2 flex items-center gap-2 lg:hidden">
  <button
    class="flex items-center gap-2 transition-colors hover:text-neutral-600"
    (click)="toggleSortDropdown()"
  >
    <span class="text-base leading-normal font-medium text-neutral-800"
      >{{ 'pages.authors.sortBy' | translate }}:</span
    >
    <span class="text-base leading-normal font-bold text-neutral-800">
      {{
        sortOrder === 'asc'
          ? ('pages.authors.ascending' | translate)
          : ('pages.authors.descending' | translate)
      }}
    </span>
    <div class="relative h-6 w-6 overflow-hidden">
      <svg
        class="h-4 w-4 transform transition-transform"
        [class.rotate-180]="showSortDropdown()"
        [attr.aria-label]="'pages.authors.expandIcon' | translate"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </div>
  </button>

  <!-- Dropdown Menu - Mobile -->
  @if (showSortDropdown()) {
    <div
      class="sort-dropdown shadow-lg absolute left-0 z-10 mt-2 rounded-lg border border-neutral-200 bg-white py-2"
      style="top: 100%"
    >
      <button
        class="w-full px-4 py-2 text-left transition-colors hover:bg-neutral-50"
        [class.font-bold]="sortOrder === 'asc'"
        (click)="setSortOrder('asc')"
      >
        <span class="text-base leading-normal text-neutral-800">
          {{ 'pages.authors.ascending' | translate }}
        </span>
      </button>
      <button
        class="w-full px-4 py-2 text-left transition-colors hover:bg-neutral-50"
        [class.font-bold]="sortOrder === 'desc'"
        (click)="setSortOrder('desc')"
      >
        <span class="text-base leading-normal text-neutral-800">
          {{ 'pages.authors.descending' | translate }}
        </span>
      </button>
    </div>
  }
</div>

<!-- Border Line -->
<div class="mb-6 border-b border-neutral-800"></div>

<!-- Authors List Container -->
<div
  class="flex flex-col"
  [infiniteScrollDistance]="1"
  [infiniteScrollThrottle]="50"
  [infiniteScrollDisabled]="false"
  [alwaysCallback]="true"
  [immediateCheck]="true"
  (scrolled)="onScroll()"
  infiniteScroll
>
  <!-- Loading State -->
  @if (loading$ | async) {
    <div class="mt-8 text-center">
      <div
        class="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-t-2 border-b-2"
      ></div>
    </div>
  }

  <!-- Empty State -->
  @if (
    (loading$ | async) === false && !error && groupedAuthors().length === 0
  ) {
    <div class="mt-8 text-center">
      <p class="text-gray-500">
        {{ 'pages.authors.noAuthorsFound' | translate }}
      </p>
    </div>
  }

  <!-- Authors List Grouped by Letter -->
  @for (group of groupedAuthors(); track trackByLetter($index, group)) {
    <!-- Letter Header -->
    <div class="mt-8 mb-4 text-2xl leading-tight font-medium text-neutral-800">
      {{ group.letter }}
    </div>
    <!-- Authors in this group -->
    <div class="flex flex-col">
      @for (author of group.authors; track trackByAuthorId($index, author)) {
        <div class="cursor-pointer" [routerLink]="['/authors', author.id]">
          <dhp-author-item [author]="author"></dhp-author-item>
        </div>
      }
    </div>
  }

  <!-- Loading More Indicator -->
  @if (loading$ | async) {
    <div class="mt-4 mb-8 text-center">
      <div
        class="border-primary mx-auto h-6 w-6 animate-spin rounded-full border-t-2 border-b-2"
      ></div>
    </div>
  }
</div>
